import type { SupabaseClient } from '../../lib/supabase';

/**
 * The Google Drive client. Uploads run in the browser as the signed-in
 * person, straight to Drive (handoff 27: binaries never pass through a
 * middleman), using the Google token that arrives alongside sign-in.
 *
 * That token is not refreshed by the app: when it expires (about an hour
 * after sign-in), Drive calls fail with 401 and every function here throws
 * 'drive_reconnect_required' — the interface answers by offering a fresh
 * sign-in, which mints a new token.
 *
 * Files land in per-project subfolders of the shared TAP root folder, so
 * everything the team uploads lives in one place the team already shares,
 * and a file's visibility in Drive follows the folder. What appears in the
 * app is still decided solely by the database (handoff 29).
 */

const DRIVE_API = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD = 'https://www.googleapis.com/upload/drive/v3';
const FOLDER_MIME = 'application/vnd.google-apps.folder';

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  /** Drive reports size as a string; folders and Docs have none. */
  size?: string;
  webViewLink?: string;
}

/**
 * The Google access token from the current session, or null when there is
 * none (never signed in with the Drive permission, or the stored session
 * predates it).
 */
export async function getDriveToken(client: SupabaseClient): Promise<string | null> {
  const { data } = await client.auth.getSession();
  return data.session?.provider_token ?? null;
}

/**
 * A freshly minted Google access token, or null when there is no stored
 * refresh token to mint one from.
 *
 * The exchange happens in the refresh-drive-token Edge Function because it
 * needs the Google client secret, which cannot live in frontend code. What
 * comes back here is only the short-lived access token — the refresh token
 * never leaves the server.
 */
export async function mintDriveToken(client: SupabaseClient): Promise<string | null> {
  try {
    const result = (await client.functions.invoke('refresh-drive-token', { body: {} })) as {
      data: { access_token?: unknown } | null;
      error: unknown;
    };
    if (result.error !== null) {
      return null;
    }
    const token = result.data?.access_token;
    return typeof token === 'string' && token !== '' ? token : null;
  } catch {
    return null;
  }
}

/**
 * Runs one piece of Drive work, and if Google refuses the token, mints a fresh
 * one and runs it again — exactly once.
 *
 * The session token is tried first because it costs nothing when it is still
 * good, which is most of the time. The retry is capped at one attempt on
 * purpose: a second refusal means the stored grant is gone rather than stale,
 * and looping would turn a reconnect prompt into a hang.
 *
 * Callers get 'drive_reconnect_required' when there is nothing left to try,
 * which is the same code they already handled before any of this existed.
 */
export async function withDriveToken<T>(
  client: SupabaseClient,
  work: (token: string) => Promise<T>,
): Promise<T> {
  const sessionToken = await getDriveToken(client);

  if (sessionToken !== null) {
    try {
      return await work(sessionToken);
    } catch (cause) {
      const code = cause instanceof Error ? cause.message : '';
      if (code !== 'drive_reconnect_required') {
        throw cause;
      }
      // Fall through: the session token has aged out, which is the whole
      // reason this path exists.
    }
  }

  const minted = await mintDriveToken(client);
  if (minted === null) {
    throw new Error('drive_reconnect_required');
  }
  return work(minted);
}

function fail(message: string): never {
  throw new Error(message);
}

async function driveFetch(token: string, url: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });

  if (response.status === 401 || response.status === 403) {
    fail('drive_reconnect_required');
  }
  if (!response.ok) {
    fail(`drive_error_${response.status}`);
  }
  return response;
}

/** Escapes a value for a Drive search query string literal. */
function escapeQueryValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * The project's subfolder under the shared root, created on first use.
 * Found by name: renaming a project starts a fresh folder for new uploads,
 * which is observable and harmless — old files keep working where they are.
 */
export async function ensureProjectFolder(
  token: string,
  rootFolderId: string,
  projectName: string,
): Promise<string> {
  const query = [
    `name = '${escapeQueryValue(projectName)}'`,
    `'${rootFolderId}' in parents`,
    `mimeType = '${FOLDER_MIME}'`,
    'trashed = false',
  ].join(' and ');

  const searchUrl =
    `${DRIVE_API}/files?q=${encodeURIComponent(query)}` +
    `&fields=${encodeURIComponent('files(id, name)')}` +
    '&supportsAllDrives=true&includeItemsFromAllDrives=true';

  const search = await driveFetch(token, searchUrl);
  const found = (await search.json()) as { files?: { id: string }[] };
  const existing = found.files?.[0];
  if (existing) {
    return existing.id;
  }

  const create = await driveFetch(token, `${DRIVE_API}/files?supportsAllDrives=true`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: projectName,
      mimeType: FOLDER_MIME,
      parents: [rootFolderId],
    }),
  });
  const created = (await create.json()) as { id: string };
  return created.id;
}

/**
 * Uploads one file with Drive's resumable protocol (handoff 27): a small
 * session-opening request, then the bytes to the session address Drive
 * hands back. The bytes go in one request — "resumable" buys the reliable
 * two-step contract and room to chunk later, not chunking today.
 */
export async function uploadFileToDrive(
  token: string,
  folderId: string,
  file: File,
): Promise<DriveFile> {
  const open = await driveFetch(
    token,
    `${DRIVE_UPLOAD}/files?uploadType=resumable&supportsAllDrives=true` +
      `&fields=${encodeURIComponent('id, name, mimeType, size, webViewLink')}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      body: JSON.stringify({
        name: file.name,
        parents: [folderId],
      }),
    },
  );

  const sessionUrl = open.headers.get('Location');
  if (!sessionUrl) {
    fail('drive_upload_session_missing');
  }

  // The session address embeds its own authorization; the bearer header is
  // still sent and harmless.
  const put = await driveFetch(token, sessionUrl, {
    method: 'PUT',
    body: file,
  });

  return (await put.json()) as DriveFile;
}

/**
 * One file's bytes, with its name and type, for a picture that has to travel
 * inside an email (docs/rich-email-design-note.md, drop B). A copy set's
 * photo lives in Drive; at send time the sender reads it here with their own
 * access and hands the bytes to the mail function, which never talks to
 * Drive itself.
 */
export async function downloadDriveFile(
  token: string,
  fileId: string,
): Promise<{ name: string; mimeType: string; bytes: Uint8Array }> {
  const meta = await driveFetch(
    token,
    `${DRIVE_API}/files/${encodeURIComponent(fileId)}?fields=${encodeURIComponent('name, mimeType')}&supportsAllDrives=true`,
  );
  const info = (await meta.json()) as { name?: string; mimeType?: string };
  const media = await driveFetch(
    token,
    `${DRIVE_API}/files/${encodeURIComponent(fileId)}?alt=media&supportsAllDrives=true`,
  );
  return {
    name: info.name ?? 'photo',
    mimeType: info.mimeType ?? 'application/octet-stream',
    bytes: new Uint8Array(await media.arrayBuffer()),
  };
}

/**
 * Makes one file viewable by anyone who has its link. Supplier sample
 * photos need this: suppliers upload into their own Drive (never TAP's
 * shared folder, which would expose everything in it), and this permission
 * is what lets the TAP side open the photo link.
 */
export async function shareFileWithLink(token: string, fileId: string): Promise<void> {
  await driveFetch(token, `${DRIVE_API}/files/${fileId}/permissions?supportsAllDrives=true`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role: 'reader', type: 'anyone' }),
  });
}

/** Refusals worth explaining, shared by the upload and link paths. */
export const DRIVE_ERROR_MESSAGES: Record<string, string> = {
  // NOT "sign out and back in": a normal sign-in does not request offline
  // access, so that advice cannot fix this and never could once the refresh
  // system landed. Only the Reconnect action asks Google for a refresh token.
  drive_reconnect_required:
    'Google Drive needs reconnecting — one click, then paste the image again.',
  drive_not_configured: 'Google Drive is not set up for this app yet. Ask an admin.',
  drive_upload_session_missing: 'Google Drive did not accept the upload. Try again.',
};
